import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Loader2, Tag, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PayPalCheckout } from "@/components/PayPalCheckout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_ARRIVAL_ID = "incognito-square-photochromic";

export const NewArrivals = ({ onPaid, featuredCode }) => {
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState("");
  const [strength, setStrength] = useState("+2.00");
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [pricing, setPricing] = useState({ subtotal: 0, savings: 0, shipping: 0, total: 0, free_shipping: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/product/${NEW_ARRIVAL_ID}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors[0].name);
      setStrength(r.data.strengths.includes("+2.00") ? "+2.00" : r.data.strengths[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (featuredCode) { setAppliedCode(featuredCode); setCode(featuredCode); }
  }, [featuredCode]);

  useEffect(() => {
    axios.post(`${API}/quote`, { product_id: NEW_ARRIVAL_ID, quantity, code: appliedCode || null, email: email || null })
      .then((r) => setPricing(r.data)).catch(() => {});
  }, [quantity, appliedCode, email]);

  const applyCode = async () => {
    const c = code.trim();
    if (!c) return;
    try {
      const r = await axios.post(`${API}/quote`, { product_id: NEW_ARRIVAL_ID, quantity, code: c, email: email || null });
      setPricing(r.data);
      if (r.data.code_valid) { setAppliedCode(c); toast.success(`Code ${r.data.discount_code} applied!`); }
      else { setAppliedCode(""); toast.error(r.data.code_error || "Invalid or expired code."); }
    } catch (e) { toast.error("Could not apply code."); }
  };

  const handleCheckout = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email for your receipt.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        product_id: NEW_ARRIVAL_ID, color, strength, quantity, email, phone: phone || null, code: appliedCode || null,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (!product) return null;
  const activeColor = product.colors.find((c) => c.name === color) || product.colors[0];

  return (
    <section id="new-arrivals" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-white/10" data-testid="new-arrivals">
      <div className="flex items-center gap-3 mb-12">
        <Sparkles className="h-5 w-5 text-[#8BA3B3]" />
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium">New Arrivals</h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <div className="rounded-2xl overflow-hidden bg-white aspect-square flex items-center justify-center relative">
            <span className="absolute top-4 left-4 z-10 text-xs uppercase tracking-widest bg-black text-white rounded-full px-3 py-1">New</span>
            <img src={activeColor.image} alt={`${product.name} — ${activeColor.name}`} className="w-full h-full object-contain p-6" data-testid="na-product-image" />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4">
            {product.colors.map((c) => (
              <button key={c.name} onClick={() => setColor(c.name)} data-testid={`na-thumb-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-xl overflow-hidden bg-white border-2 transition-colors ${color === c.name ? "border-white" : "border-transparent hover:border-white/40"}`}>
                <img src={c.image} alt={c.name} className="w-full h-full object-contain p-2 aspect-square" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-white text-white" />)}
            <span className="text-sm text-white/50 ml-1">New · be the first to review</span>
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl tracking-tighter font-medium">{product.name}</h3>
          <div className="flex items-baseline gap-4 mt-5">
            <span className="text-3xl font-semibold" data-testid="na-price">${product.price}</span>
            <span className="text-lg text-white/40 line-through">${product.compare_at_price}</span>
          </div>
          <p className="mt-6 text-white/60 leading-relaxed">{product.description}</p>

          <div className="mt-8">
            <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Frame color — <span className="text-white">{color}</span></p>
            <div className="flex flex-wrap gap-3" data-testid="na-color-group">
              {product.colors.map((c) => (
                <button key={c.name} onClick={() => setColor(c.name)} data-testid={`na-color-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-full border px-5 py-2.5 text-sm transition-colors ${color === c.name ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Magnification — <span className="text-white">{strength}</span></p>
            <div className="flex flex-wrap gap-3" data-testid="na-strength-group">
              {product.strengths.map((s) => (
                <button key={s} onClick={() => setStrength(s)} data-testid={`na-strength-${s.replace(/[+.]/g, "")}`}
                  className={`rounded-full border px-5 py-2.5 text-sm transition-colors ${strength === s ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Quantity</p>
            <div className="inline-flex items-center rounded-full border border-white/20">
              <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} data-testid="na-qty-decrease"
                className="h-11 w-11 text-xl hover:bg-white/10 rounded-l-full transition-colors">−</button>
              <span className="w-12 text-center text-lg" data-testid="na-qty-value">{quantity}</span>
              <button onClick={() => setQuantity((q) => Math.min(10, q + 1))} data-testid="na-qty-increase"
                className="h-11 w-11 text-xl hover:bg-white/10 rounded-r-full transition-colors">+</button>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Promo code</p>
            <div className="flex gap-3 max-w-md">
              <Input value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="Enter code" data-testid="na-promo-input"
                className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30 uppercase" />
              <Button onClick={applyCode} data-testid="na-promo-apply" variant="outline"
                className="h-12 rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 px-6 shrink-0">
                Apply
              </Button>
            </div>
          </div>

          <div className="mt-8 rounded-2xl bg-[#121212] border border-white/10 p-6 space-y-3" data-testid="na-price-breakdown">
            <div className="flex justify-between text-white/70"><span>Subtotal</span><span data-testid="na-subtotal">${pricing.subtotal.toFixed(2)}</span></div>
            {pricing.savings > 0 && (
              <div className="flex justify-between text-emerald-400"><span className="flex items-center gap-2"><Tag className="h-4 w-4" /> BOGO savings</span><span data-testid="na-savings">−${pricing.savings.toFixed(2)}</span></div>
            )}
            {pricing.discount_amount > 0 && (
              <div className="flex justify-between text-emerald-400"><span className="flex items-center gap-2"><Tag className="h-4 w-4" /> Code {pricing.discount_code}</span><span data-testid="na-discount">−${pricing.discount_amount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between text-white/70"><span>Shipping</span><span data-testid="na-shipping">{pricing.free_shipping ? "FREE" : `$${pricing.shipping.toFixed(2)}`}</span></div>
            <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-semibold"><span>Total</span><span data-testid="na-total">${pricing.total.toFixed(2)}</span></div>
          </div>

          <div className="mt-6">
            <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Email for receipt</p>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" data-testid="na-checkout-email"
              className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30 max-w-md" />
          </div>

          <div className="mt-4">
            <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Phone <span className="text-white/30 normal-case tracking-normal">(optional)</span></p>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 000-0000" data-testid="na-checkout-phone"
              className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30 max-w-md" />
          </div>

          <Button onClick={handleCheckout} disabled={loading} data-testid="na-checkout-button"
            className="mt-6 w-full rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base font-semibold">
            {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Redirecting…</> : <>Buy with Card — ${pricing.total.toFixed(2)}</>}
          </Button>

          <div className="flex items-center gap-4 my-4">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-white/40">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <PayPalCheckout productId={NEW_ARRIVAL_ID} color={color} strength={strength}
            quantity={quantity} email={email} phone={phone} code={appliedCode} onSuccess={() => onPaid?.()} />

          <p className="mt-4 text-xs text-white/40" data-testid="na-shipping-note">
            Allow 8–15 business days for order processing and shipping.
          </p>
        </div>
      </div>
    </section>
  );
};
